/**
 * Wave-3 replay engine — pure timeline logic for replaying a finished
 * orchestrator run on the Bridge. No React, no I/O: everything here is a
 * function of the REAL FleetSession / FleetJournalEntry inputs (the same
 * shapes the Fleet HUD reads from /api/v1/fleet-hud/*). Nothing is
 * synthesized — stateAt() time-slices the input data at a virtual clock time.
 *
 * Timestamp caveat (load-bearing): FleetSession.started_at / completed_at and
 * FleetJournalEntry.ts arrive as ISO/str(datetime) STRINGS. Every comparison
 * against the numeric virtual clock goes through parseTs() (fleetTime.ts),
 * which handles both "…T…Z" and the space-separated str(datetime) shape.
 */
import type {
  FleetJournalEntry,
  FleetOverview,
  FleetSession,
} from '../../../services/fleetHudApi';
import { deriveMood, type FleetMood } from '../../fleet/fleetMood';
import { parseTs } from '../../fleet/fleetTime';

export type ReplayEvent =
  | { t: number /* epochMs */; kind: 'session_open'; session: FleetSession }
  | { t: number /* epochMs */; kind: 'session_close'; session: FleetSession }
  | { t: number /* epochMs */; kind: 'journal'; entry: FleetJournalEntry };

export interface ReplaySpan {
  start: number;
  end: number;
}

export interface ReplayState {
  sessionsVisible: FleetSession[];
  journalVisible: FleetJournalEntry[];
  mood: FleetMood;
}

export interface ReplayTimeline {
  events: ReplayEvent[];
  span: ReplaySpan;
  autoSpeed: number;
  stateAt: (tVirtual: number) => ReplayState;
}

/** run_id the replay represents — the most frequent run_id across sessions. */
function dominantRunId(sessions: FleetSession[]): string | null {
  const counts = new Map<string, number>();
  for (const s of sessions) {
    if (!s.run_id) continue;
    counts.set(s.run_id, (counts.get(s.run_id) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [id, n] of counts) {
    if (n > bestN) {
      best = id;
      bestN = n;
    }
  }
  return best;
}

export function buildTimeline(
  sessions: FleetSession[],
  entries: FleetJournalEntry[]
): ReplayTimeline {
  const events: ReplayEvent[] = [];

  for (const session of sessions) {
    const start = parseTs(session.started_at);
    const end = parseTs(session.completed_at);
    // In-progress sessions (completed_at null) are excluded from the replay —
    // a close event at NaN would silently corrupt the sort order.
    if (start === null || end === null) continue;
    events.push({ t: start, kind: 'session_open', session });
    events.push({ t: end, kind: 'session_close', session });
  }
  for (const entry of entries) {
    const t = parseTs(entry.ts);
    if (t === null) continue;
    events.push({ t, kind: 'journal', entry });
  }

  // Stable sort: for equal t the push order (open before close) is preserved.
  events.sort((a, b) => a.t - b.t);

  const span: ReplaySpan =
    events.length === 0
      ? { start: 0, end: 0 }
      : { start: events[0].t, end: events[events.length - 1].t };

  // Virtual-ms per real-ms for a ~25s playback, clamped [10, 100].
  const autoSpeed = Math.round(
    Math.min(100, Math.max(10, (span.end - span.start) / 25000))
  );

  const runId = dominantRunId(sessions);

  const stateAt = (tVirtual: number): ReplayState => {
    const sessionsVisible: FleetSession[] = [];
    for (const session of sessions) {
      const start = parseTs(session.started_at);
      if (start === null || start > tVirtual) continue;
      const end = parseTs(session.completed_at);
      if (end !== null && end < tVirtual) continue;
      // Open at t (started, not yet closed — or never closed): the returned
      // copy reads as in-progress, exactly what deriveMood expects.
      sessionsVisible.push({ ...session, completed_at: null });
    }

    const journalVisible: FleetJournalEntry[] = [];
    let latestEntryT = 0;
    for (const entry of entries) {
      const t = parseTs(entry.ts);
      if (t === null || t > tVirtual) continue;
      journalVisible.push(entry);
      if (t > latestEntryT) latestEntryT = t;
    }

    // Time-sliced overview in the exact shape deriveMood consumes: the run is
    // active while any of its sessions is still open at tVirtual, and the
    // journal age is measured against the replay clock, not Date.now().
    const overview: FleetOverview = {
      orchestrator_alive: true,
      journal_age_seconds: (tVirtual - latestEntryT) / 1000,
      latest_run: runId
        ? {
            run_id: runId,
            status: sessionsVisible.length > 0 ? 'in_progress' : 'completed',
          }
        : null,
      open_escalations: 0,
    };

    return {
      sessionsVisible,
      journalVisible,
      mood: deriveMood(overview, [], sessionsVisible),
    };
  };

  return { events, span, autoSpeed, stateAt };
}
